import { assistantCitationsToPlainText } from "@cmmd-forge/shared/assistantCitations";
import {
  DEFAULT_MODEL_BY_PROVIDER,
  type CommandId,
  type EnvironmentApi,
  type EnvironmentId,
  type MessageId,
  type ModelSelection,
  type ProviderInteractionMode,
  RuntimeMode,
  type ServerProvider,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { buildTemporaryWorktreeBranchName } from "@cmmd-forge/shared/git";
import { truncate } from "@cmmd-forge/shared/String";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type DraftThreadState,
} from "../../composerDraftStore";
import { getStoredLocalDeviceRuntimeId } from "../../deviceRegistryIdentity";
import {
  isLocalExecutionEnvironmentId,
  resolveLocalDeviceRuntimeId,
} from "../../environments/localExecutionEnvironment";
import { readPrimaryEnvironmentDeviceRuntimeId } from "../../environments/primary/context";
import { prepareComposerFileAttachments } from "../../lib/prepareComposerFileAttachments";
import { formatTerminalContextLabel, type TerminalContextDraft } from "../../lib/terminalContext";
import { type Project, type Thread } from "../../types";
import { readFileAsDataUrl } from "../ChatView.logic";
import { toastManager } from "../ui/toast";
import { newCommandId } from "~/lib/utils";
import { createCloudMirrorGate } from "./cloudMirrorGate";

// Falls back to the first attachment's name, or the first terminal context's
// label, when the prompt itself is empty.
function resolveTurnTitle(input: {
  readonly composerImagesSnapshot: readonly ComposerImageAttachment[];
  readonly composerFilesSnapshot: readonly ComposerFileAttachment[];
  readonly composerTerminalContextsSnapshot: readonly TerminalContextDraft[];
  readonly trimmedPrompt: string;
}): string {
  const firstComposerImageName = input.composerImagesSnapshot[0]?.name ?? null;
  const firstComposerFileName = input.composerFilesSnapshot[0]?.name ?? null;
  const fallbackTitleSeed =
    firstComposerImageName ??
    firstComposerFileName ??
    (input.composerTerminalContextsSnapshot.length > 0
      ? formatTerminalContextLabel(input.composerTerminalContextsSnapshot[0]!)
      : "New thread");
  return truncate(
    assistantCitationsToPlainText(input.trimmedPrompt).replace(/\s+/g, " ") || fallbackTitleSeed,
  );
}

// The project's stored defaultModelSelection.model is only usable here when
// it was set FOR the currently selected provider. Model slugs are
// provider-scoped (codex's "gpt-5.6-sol" is meaningless to apex, which
// expects e.g. "apex-flash"), and a fresh cloud runtime resets a project's
// defaultModelSelection back to the codex default on every bootstrap
// (recoverCloudProjects), independent of whichever provider is currently
// selected in the composer. Falling back to a cross-provider model here
// produces an unusable {provider, model} pairing that the runtime can never
// authenticate (it doesn't match any of that provider's known models).
function resolveThreadCreateModelSelection(input: {
  readonly activeProject: Project;
  readonly selectedProvider: ModelSelection["provider"];
  readonly selectedModel: string | null;
  readonly selectedModelSelection: ModelSelection;
}): ModelSelection {
  const projectDefaultModel =
    input.activeProject.defaultModelSelection?.provider === input.selectedProvider
      ? input.activeProject.defaultModelSelection.model
      : undefined;
  return {
    provider: input.selectedProvider,
    model:
      input.selectedModel ||
      projectDefaultModel ||
      DEFAULT_MODEL_BY_PROVIDER[input.selectedProvider],
    ...("providerAccountId" in input.selectedModelSelection &&
    input.selectedModelSelection.providerAccountId
      ? { providerAccountId: input.selectedModelSelection.providerAccountId }
      : {}),
    ...(input.selectedModelSelection.options
      ? { options: input.selectedModelSelection.options }
      : {}),
  } as ModelSelection;
}

// A local draft thread carries its own pending CMMD/anchor fields until the
// server thread exists; once it's a server thread those fields already live
// on the thread itself and this turn doesn't need to resend them.
function resolveTurnAnchorFields(input: {
  readonly activeThread: Thread;
  readonly draftThread: DraftThreadState | null | undefined;
  readonly isLocalDraftThread: boolean;
}): {
  readonly cmmdTaskIdForTurn: string | null | undefined;
  readonly anchorKindForTurn: Thread["anchorKind"] | undefined;
  readonly anchorCmmdIdForTurn: string | null | undefined;
} {
  const cmmdTaskIdForTurn =
    input.isLocalDraftThread && input.draftThread?.cmmdTaskId
      ? input.draftThread.cmmdTaskId
      : undefined;
  const anchorKindForTurn =
    input.isLocalDraftThread && input.draftThread?.anchorKind
      ? input.draftThread.anchorKind
      : input.isLocalDraftThread
        ? input.activeThread.anchorKind
        : undefined;
  const anchorCmmdIdForTurn =
    input.isLocalDraftThread && input.draftThread?.anchorCmmdId !== undefined
      ? input.draftThread.anchorCmmdId
      : input.isLocalDraftThread
        ? input.activeThread.anchorCmmdId
        : undefined;
  return { cmmdTaskIdForTurn, anchorKindForTurn, anchorCmmdIdForTurn };
}

// Resolves the title, model selection, and CMMD/anchor fields a new turn
// carries, before any network calls fire.
function resolveTurnHeadFields(input: {
  readonly activeProject: Project;
  readonly activeThread: Thread;
  readonly draftThread: DraftThreadState | null | undefined;
  readonly composerImagesSnapshot: readonly ComposerImageAttachment[];
  readonly composerFilesSnapshot: readonly ComposerFileAttachment[];
  readonly composerTerminalContextsSnapshot: readonly TerminalContextDraft[];
  readonly trimmedPrompt: string;
  readonly selectedProvider: ModelSelection["provider"];
  readonly selectedModel: string | null;
  readonly selectedModelSelection: ModelSelection;
  readonly isLocalDraftThread: boolean;
}): {
  readonly title: string;
  readonly threadCreateModelSelection: ModelSelection;
  readonly cmmdTaskIdForTurn: string | null | undefined;
  readonly anchorKindForTurn: Thread["anchorKind"] | undefined;
  readonly anchorCmmdIdForTurn: string | null | undefined;
} {
  const title = resolveTurnTitle(input);
  const threadCreateModelSelection = resolveThreadCreateModelSelection(input);
  return {
    title,
    threadCreateModelSelection,
    ...resolveTurnAnchorFields(input),
  };
}

// Builds the optional bootstrap payload that rides along with the first
// thread.turn.start dispatch: thread creation for a local draft, and/or a
// worktree prepare request when the turn needs to fork one.
function buildTurnBootstrap(input: {
  readonly isLocalDraftThread: boolean;
  readonly shouldCreateWorktree: boolean;
  readonly activeProject: Project;
  readonly activeThread: Thread;
  readonly draftThread: DraftThreadState | null | undefined;
  readonly title: string;
  readonly threadCreateModelSelection: ModelSelection;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly activeThreadBranch: string | null;
  readonly baseBranchForWorktree: string | null;
  readonly anchorKindForTurn: Thread["anchorKind"] | undefined;
  readonly anchorCmmdIdForTurn: string | null | undefined;
}) {
  if (!input.isLocalDraftThread && !input.shouldCreateWorktree) {
    return undefined;
  }
  return {
    ...(input.isLocalDraftThread
      ? {
          createThread: {
            projectId: input.activeProject.id,
            title: input.title,
            modelSelection: input.threadCreateModelSelection,
            runtimeMode: input.runtimeMode,
            interactionMode: input.interactionMode,
            branch: input.activeThreadBranch,
            worktreePath: input.activeThread.worktreePath,
            ...(input.anchorKindForTurn !== undefined
              ? { anchorKind: input.anchorKindForTurn }
              : {}),
            ...(input.anchorCmmdIdForTurn !== undefined
              ? { anchorCmmdId: input.anchorCmmdIdForTurn }
              : {}),
            linkedPullRequest: input.draftThread?.linkedPullRequest ?? null,
            createdAt: input.activeThread.createdAt,
          },
        }
      : {}),
    // baseBranch may be null here: the server resolves the repo default
    // branch itself when it receives null, so a worktree request never
    // has to wait on the client's own branch resolution.
    ...(input.shouldCreateWorktree
      ? {
          prepareWorktree: {
            projectCwd: input.activeProject.cwd,
            baseBranch: input.baseBranchForWorktree,
            branch: buildTemporaryWorktreeBranchName(),
          },
          runSetupScript: true,
        }
      : {}),
  };
}

export async function dispatchChatTurnStart(input: {
  readonly signal?: AbortSignal;
  readonly onDispatch?: () => void;
  readonly api: EnvironmentApi;
  readonly environmentId: EnvironmentId;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly activeProject: Project;
  readonly activeThread: Thread;
  readonly draftThread: DraftThreadState | null | undefined;
  readonly threadIdForSend: ThreadId;
  readonly messageIdForSend: MessageId;
  readonly commandIdForSend: CommandId;
  readonly messageCreatedAt: string;
  readonly trimmedPrompt: string;
  readonly composerImagesSnapshot: readonly ComposerImageAttachment[];
  readonly composerFilesSnapshot: readonly ComposerFileAttachment[];
  readonly composerTerminalContextsSnapshot: readonly TerminalContextDraft[];
  readonly outgoingMessageText: string;
  readonly selectedProvider: ModelSelection["provider"];
  readonly selectedModel: string | null;
  readonly selectedProviderModels: ReadonlyArray<ServerProvider["models"][number]>;
  readonly selectedPromptEffort: string | null;
  readonly selectedModelSelection: ModelSelection;
  readonly isFirstMessage: boolean;
  readonly isServerThread: boolean;
  readonly isLocalDraftThread: boolean;
  readonly shouldCreateWorktree: boolean;
  readonly baseBranchForWorktree: string | null;
  readonly activeThreadBranch: string | null;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly beginLocalDispatch: (options?: { readonly preparingWorktree?: boolean }) => void;
  readonly persistThreadSettingsForNextTurn: (settings: {
    readonly threadId: ThreadId;
    readonly createdAt: string;
    readonly modelSelection?: ModelSelection;
    readonly runtimeMode: RuntimeMode;
    readonly interactionMode: ProviderInteractionMode;
  }) => Promise<void>;
}): Promise<void> {
  const {
    title,
    threadCreateModelSelection,
    cmmdTaskIdForTurn,
    anchorKindForTurn,
    anchorCmmdIdForTurn,
  } = resolveTurnHeadFields({
    activeProject: input.activeProject,
    activeThread: input.activeThread,
    draftThread: input.draftThread,
    composerImagesSnapshot: input.composerImagesSnapshot,
    composerFilesSnapshot: input.composerFilesSnapshot,
    composerTerminalContextsSnapshot: input.composerTerminalContextsSnapshot,
    trimmedPrompt: input.trimmedPrompt,
    selectedProvider: input.selectedProvider,
    selectedModel: input.selectedModel,
    selectedModelSelection: input.selectedModelSelection,
    isLocalDraftThread: input.isLocalDraftThread,
  });

  if (input.isFirstMessage && input.isServerThread) {
    await input.api.orchestration.dispatchCommand({
      type: "thread.meta.update",
      commandId: newCommandId(),
      threadId: input.threadIdForSend,
      title,
    });
  }

  if (input.isServerThread) {
    await input.persistThreadSettingsForNextTurn({
      threadId: input.threadIdForSend,
      createdAt: input.messageCreatedAt,
      ...(input.selectedModel ? { modelSelection: input.selectedModelSelection } : {}),
      runtimeMode: input.runtimeMode,
      interactionMode: input.interactionMode,
    });
  }

  // Read each image individually so a single unreadable file only removes that
  // attachment from the turn rather than failing the entire dispatch.
  const turnAttachmentResults = await Promise.all(
    input.composerImagesSnapshot.map(async (image) => {
      try {
        return {
          type: "image" as const,
          name: image.name,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          dataUrl: await readFileAsDataUrl(image.file),
        };
      } catch {
        toastManager.add({
          type: "error",
          title: "Attachment skipped",
          description: `Could not read "${image.name}" — the file was removed or is unreadable.`,
        });
        return null;
      }
    }),
  );
  const turnAttachments = turnAttachmentResults.filter(
    (r): r is NonNullable<typeof r> => r !== null,
  );
  // A local execution never mirrors (forge#3516): the cloud lane exists so an
  // attachment outlives its runtime, which a run on this machine does not
  // need, and locally produced files must not leave the machine. The
  // route-level isLocalDraftThread flag is the wrong predicate here; the
  // shared execution-environment rule is the one the draft picker and the
  // submission resolver already use.
  const targetIsThisMachine = isLocalExecutionEnvironmentId({
    environmentId: input.environmentId,
    primaryEnvironmentId: input.primaryEnvironmentId,
    localDeviceRuntimeId: resolveLocalDeviceRuntimeId({
      serverReported: readPrimaryEnvironmentDeviceRuntimeId(),
      stored: getStoredLocalDeviceRuntimeId(),
    }),
  });
  const cloudMirrorGate = createCloudMirrorGate();
  try {
    const fileAttachments = await prepareComposerFileAttachments({
      environmentId: input.environmentId,
      files: input.composerFilesSnapshot,
      ...(input.signal ? { signal: input.signal } : {}),
      // Best-effort second copy in CMMD object storage (forge#3516): never on
      // a local execution, never before the turn itself is accepted, and
      // never blocking the send.
      ...(targetIsThisMachine
        ? {}
        : {
            cloudMirror: {
              threadId: input.threadIdForSend,
              accepted: cloudMirrorGate.accepted,
            },
          }),
    });
    const bootstrap = buildTurnBootstrap({
      isLocalDraftThread: input.isLocalDraftThread,
      shouldCreateWorktree: input.shouldCreateWorktree,
      activeProject: input.activeProject,
      activeThread: input.activeThread,
      draftThread: input.draftThread,
      title,
      threadCreateModelSelection,
      runtimeMode: input.runtimeMode,
      interactionMode: input.interactionMode,
      activeThreadBranch: input.activeThreadBranch,
      baseBranchForWorktree: input.baseBranchForWorktree,
      anchorKindForTurn,
      anchorCmmdIdForTurn,
    });
    input.signal?.throwIfAborted();
    input.beginLocalDispatch({ preparingWorktree: false });
    input.onDispatch?.();
    await input.api.orchestration.dispatchCommand({
      type: "thread.turn.start",
      commandId: input.commandIdForSend,
      threadId: input.threadIdForSend,
      message: {
        messageId: input.messageIdForSend,
        role: "user",
        text: input.outgoingMessageText,
        attachments: [...turnAttachments, ...fileAttachments],
      },
      modelSelection: input.selectedModelSelection,
      titleSeed: title,
      runtimeMode: input.runtimeMode,
      interactionMode: input.interactionMode,
      ...(bootstrap ? { bootstrap } : {}),
      ...(cmmdTaskIdForTurn ? { cmmdTaskId: cmmdTaskIdForTurn } : {}),
      ...(anchorKindForTurn ? { anchorKind: anchorKindForTurn } : {}),
      ...(anchorCmmdIdForTurn !== undefined ? { anchorCmmdId: anchorCmmdIdForTurn } : {}),
      createdAt: input.messageCreatedAt,
    });
    cloudMirrorGate.accept();
  } catch (error) {
    cloudMirrorGate.cancel(error);
    throw error;
  }
}
